import { Directive, ElementRef, OnDestroy, afterNextRender, effect, inject } from '@angular/core';
import { LocalizationService } from './localization.service';

@Directive({ selector: '[tmsLocalize]', standalone: true })
export class LocalizeDirective implements OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>).nativeElement;
  private readonly localization = inject(LocalizationService);
  private readonly textSources = new WeakMap<Text, string>();
  private readonly attributeSources = new WeakMap<Element, Map<string, string>>();
  private observer?: MutationObserver;
  private applying = false;

  constructor() {
    effect(() => {
      this.localization.language();
      this.translateTree(this.host);
    });
    afterNextRender(() => {
      this.translateTree(this.host);
      this.observer = new MutationObserver((records) => {
        if (this.applying) return;
        this.applying = true;
        this.observer?.disconnect();
        try {
          for (const record of records) {
            if (record.type === 'characterData') this.translateText(record.target as Text, true);
            record.addedNodes.forEach((node) => this.translateTree(node));
            if (record.type === 'attributes')
              this.translateAttributes(record.target as Element, true);
          }
        } finally {
          this.applying = false;
          this.observe();
        }
      });
      this.observe();
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private translateTree(root: Node): void {
    this.applying = true;
    try {
      if (root.nodeType === Node.TEXT_NODE) this.translateText(root as Text);
      if (root.nodeType === Node.ELEMENT_NODE) {
        this.translateAttributes(root as Element);
        const walker = document.createTreeWalker(
          root,
          NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
        );
        let node: Node | null;
        while ((node = walker.nextNode()))
          node.nodeType === Node.TEXT_NODE
            ? this.translateText(node as Text)
            : this.translateAttributes(node as Element);
      }
    } finally {
      this.observer?.takeRecords();
      this.applying = false;
    }
  }

  private translateText(node: Text, refreshSource = false): void {
    const current = node.data;
    if (!current.trim()) return;
    if (refreshSource || !this.textSources.has(node)) this.textSources.set(node, current);
    const source = this.textSources.get(node)!;
    const leading = source.match(/^\s*/)?.[0] ?? '';
    const trailing = source.match(/\s*$/)?.[0] ?? '';
    const translated = leading + this.localization.translate(source.trim()) + trailing;
    if (node.data !== translated) node.data = translated;
  }

  private translateAttributes(element: Element, refreshSource = false): void {
    const attributes = ['placeholder', 'title', 'aria-label'];
    let sources = this.attributeSources.get(element);
    if (!sources) {
      sources = new Map();
      this.attributeSources.set(element, sources);
    }
    for (const name of attributes) {
      const current = element.getAttribute(name);
      if (!current) continue;
      if (refreshSource || !sources.has(name)) sources.set(name, current);
      const translated = this.localization.translate(sources.get(name)!);
      if (current !== translated) element.setAttribute(name, translated);
    }
  }

  private observe(): void {
    this.observer?.observe(this.host, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['placeholder', 'title', 'aria-label'],
    });
  }
}
